import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import { requestAccess } from '../investors/api';

const TYPES = ['Angel Investor','Family Office','Venture Capital','Private Equity','Corporate / Strategic Investor','Adviser','Other'];

const V2InvestorRequestAccess = () => {
  useSEO({
    title: 'Request Investor Access, Keystone',
    description: 'Request confidential access to the Keystone / Videra investor portal.',
    path: '/investors/request-access',
    noindex: true,
  });

  const [state, setState] = useState({ submitting: false, done: false, error: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ submitting: true, done: false, error: '' });
    const f = new FormData(e.currentTarget);
    try {
      await requestAccess({
        full_name: f.get('full_name'),
        company: f.get('company'),
        job_title: f.get('job_title') || '',
        email: f.get('email'),
        telephone: f.get('telephone') || null,
        country: f.get('country') || null,
        investor_type: f.get('investor_type') || 'Other',
        areas_of_interest: f.get('areas_of_interest') || null,
        message: f.get('message') || null,
        accept: f.get('accept') === 'on',
      });
      setState({ submitting: false, done: true, error: '' });
    } catch (err) {
      setState({ submitting: false, done: false, error: err.message });
    }
  };

  if (state.done) {
    return (
      <div data-testid="v2-investor-request-done">
        <ProductHero
          eyebrow="Investors"
          title="Thanks, request received."
          body="A member of the Keystone team will review your request and be in touch. Access is subject to approval, and all information within the Investor Portal is confidential."
          primary={{ label: 'Back to Investors', to: '/investors' }}
          testid="investor-request-done-hero"
        />
      </div>
    );
  }

  return (
    <div data-testid="v2-investor-request">
      <ProductHero
        eyebrow="Investors · Request access"
        title="Request Investor Portal access."
        body="Provide a few details so we can verify your interest. Access is subject to approval and all portal information is confidential."
        testid="investor-request-hero"
      />
      <section className="py-12 md:py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-5" data-testid="investor-request-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" name="full_name" required />
              <Field label="Company / Fund" name="company" required />
              <Field label="Job title" name="job_title" />
              <Field label="Business email" name="email" type="email" required />
              <Field label="Telephone" name="telephone" />
              <Field label="Country" name="country" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Investor type</label>
              <select name="investor_type" className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="investor-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Areas of interest</label>
              <input name="areas_of_interest" placeholder="e.g. Healthcare · PFI · Firestopping" className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Investment / Partnership interest, message</label>
              <textarea name="message" rows={5} className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" name="accept" required className="mt-1" data-testid="investor-accept" />
              <span>I understand that access is subject to approval and that information within the Investor Portal is confidential.</span>
            </label>
            {state.error && <p className="text-sm text-red-600" data-testid="investor-request-error">{state.error}</p>}
            <button type="submit" disabled={state.submitting} className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors" data-testid="investor-request-submit">
              {state.submitting ? 'Sending…' : (<>Request Access <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
          <p className="mt-6 text-xs text-slate-500">
            Already approved? <Link to="/investors/login" className="text-blue-600 hover:text-blue-700">Investor Login</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = 'text', required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">
      {label}{!required && <span className="text-slate-400 ml-1 normal-case tracking-normal">— optional</span>}
    </label>
    <input name={name} type={type} required={required} className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid={`investor-req-${name}`} />
  </div>
);

export default V2InvestorRequestAccess;
